import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const portfolioAPI = {
  // Get all stocks in portfolio for a specific user
  getAllStocks: async (userEmail = null) => {
    try {
      const params = userEmail ? { user_email: userEmail } : {};
      const response = await axios.get(`${API}/portfolio`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      throw error;
    }
  },

  // Get aggregate portfolio (all stocks from all users combined) - for viewer role
  getAggregatePortfolio: async () => {
    try {
      const response = await axios.get(`${API}/portfolio/aggregate`);
      return response.data;
    } catch (error) {
      console.error('Error fetching aggregate portfolio:', error);
      throw error;
    }
  },

  // Add a new stock for a specific user
  addStock: async (symbol, shares, costBasis, platform = 'Unknown', currentPrice = null, userEmail = null, investmentType = 'STOCK') => {
    try {
      const payload = {
        symbol: symbol.toUpperCase(),
        shares: parseFloat(shares),
        costBasis: parseFloat(costBasis),
        platform: platform || 'Unknown',
        investmentType: investmentType || 'STOCK'
      };
      
      // Only include currentPrice if provided
      if (currentPrice) {
        payload.currentPrice = parseFloat(currentPrice);
      }
      
      const params = userEmail ? { user_email: userEmail } : {};
      const response = await axios.post(`${API}/portfolio`, payload, { params });
      return response.data;
    } catch (error) {
      console.error('Error adding stock:', error);
      throw error;
    }
  },

  // Delete a stock (with user verification)
  deleteStock: async (stockId, userEmail = null) => {
    try {
      const params = userEmail ? { user_email: userEmail } : {};
      const response = await axios.delete(`${API}/portfolio/${stockId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error deleting stock:', error);
      throw error;
    }
  },

  // Get current price for a symbol
  getStockPrice: async (symbol) => {
    try {
      const response = await axios.get(`${API}/stock-price/${symbol}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stock price:', error);
      throw error;
    }
  },
  
  // Get investment types
  getInvestmentTypes: async () => {
    try {
      const response = await axios.get(`${API}/investment-types`);
      return response.data;
    } catch (error) {
      console.error('Error fetching investment types:', error);
      throw error;
    }
  },
  
  // Get portfolio performance for a specific user
  getPerformance: async (userEmail = null) => {
    try {
      const params = userEmail ? { user_email: userEmail } : {};
      const response = await axios.get(`${API}/portfolio/performance`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching performance:', error);
      throw error;
    }
  },

  // Export portfolio to Excel
  exportPortfolio: async (userEmail = null, platform = null) => {
    try {
      const params = {};
      if (userEmail) params.user_email = userEmail;
      if (platform) params.platform = platform;
      const response = await axios.get(`${API}/portfolio/export`, { 
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = platform 
        ? `portfolio_${platform.replace(/\s+/g, '_')}_export_${new Date().toISOString().slice(0,10)}.xlsx`
        : `portfolio_export_${new Date().toISOString().slice(0,10)}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error exporting portfolio:', error);
      throw error;
    }
  },

  // Import portfolio from Excel file
  importPortfolio: async (file, userEmail = null, platform = null, replace = false) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const params = {};
      if (userEmail) params.user_email = userEmail;
      if (platform) params.platform = platform;
      if (replace) params.replace = true;
      
      const response = await axios.post(`${API}/portfolio/import`, formData, {
        params,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error importing portfolio:', error);
      throw error;
    }
  }
};